import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useMemo,
  useState
} from 'react';

import {
  TextField,
  InputProps,
  FormControl,
  Typography,
  FormGroup,
  IconButton
} from '@mui/material';

import {
  Controller, FieldErrors, UseFormReturn, useForm, useWatch
} from 'react-hook-form';

import clsx from 'clsx';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import InfoRail from '@/common/components/InfoRail';
import Label from '@/shared/molecules/Label';

import css from './Input.module.css';

interface BaseInputProps {
  id: string;
  testId?: string;
  value?: string;
  placeholder?: string;
  onChange?: (input: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | FormEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  icon?: InputProps;
  defaultValue?: string;
  name?: string;
  inputRef?: any;
  error?: boolean;
  onKeyPress?: (event: any) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  autoComplete?: string;
  helperText?: string;
  autoFocus?: boolean;
  maxLength?: number;
  minLength?: number;
  type?: string;
  helperTextClassName?: string;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  ariaLabel?: string;
  required?: boolean;
  invalidMessage?: string;
  label?: string;
  multiline?: boolean;
  minRows?: number;
  labelClass?: string;
  control?: UseFormReturn['control'];
  errors?: FieldErrors;
  variant?: 'outlined' | 'filled' | 'standard';
  fullWidth?: boolean;
  showCharacterCount?: boolean;
  extraInformation?: ContactFormFieldExtraInformation;
  showOptionalLabel?: boolean;
  startAdornment?: React.ReactNode;
  pattern?: string;
  optional?: boolean;
  containerWidth?: string;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const Input = ({
  testId,
  name,
  icon,
  defaultValue,
  inputRef,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  error,
  onKeyPress,
  onFocus,
  onBlur,
  autoComplete,
  autoFocus,
  maxLength,
  minLength,
  helperTextClassName = '',
  disabled,
  ariaLabel,
  readOnly,
  required = false,
  invalidMessage,
  label,
  multiline,
  minRows,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  labelClass,
  control,
  errors,
  helperText,
  variant = 'outlined',
  fullWidth = false,
  showCharacterCount = false,
  extraInformation,
  showOptionalLabel,
  startAdornment,
  pattern,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  optional,
  containerWidth,
  ...rest
}: BaseInputProps) => {
  const fieldName = name || rest?.id || 'defaultName';
  const fieldError = errors?.[fieldName];
  const hasError = Boolean(fieldError?.type);
  const [showingExtraInformation, setShowingExtraInformation] = useState<boolean>(false);
  const { control: defaultControl } = useForm();
  const baseControl = control || defaultControl;

  const watchedValue = useWatch({
    control: baseControl,
    name: fieldName,
    defaultValue: defaultValue || ''
  });

  const onKeyDownCallback = useCallback((event: any) => {
    if (onKeyPress) {
      onKeyPress(event);
    }
  }, [onKeyPress]);

  const characterCount = useMemo((): number => Math.abs((maxLength ?? 0) - (watchedValue?.length ?? 0)), [watchedValue, maxLength]);

  const computedHelperText = useMemo(() => {
    if (hasError) {
      return fieldError?.message?.toString() || helperText;
    }

    if (showCharacterCount) {
      return `${characterCount} characters remaining`;
    }

    return helperText;
  }, [
    characterCount,
    fieldError,
    hasError,
    helperText,
    showCharacterCount
  ]);

  const onExtraInformationIconClicked = useCallback(() => {
    setShowingExtraInformation(!showingExtraInformation);
  }, [showingExtraInformation]);

  const minLengthProp = minLength ? { minLength } : {};
  const maxLengthProp = maxLength ? { maxLength } : {};

  const extraInformationWithLinks = useMemo(() => extraInformation?.links?.reduce((text: string, link: FieldExtraInformationLink) => {
    // TODO_: there has to be a better way to do this, maybe overengineered, consider moving to data as markup
    const markup = (
      `<a class="${css.link}"  href="${link?.url}" ${link?.external ? ' target="_blank" rel="noopener noreferrer"' : ''}>${link?.label}</a>`
    );

    return text?.replace(link?.pattern, markup);
  }, extraInformation?.text), [extraInformation]);

  const renderInput = (field: any) => (
      <TextField
        {...field}
        {...rest}
        name={fieldName}
        id={field?.id}
        value={watchedValue}
        error={hasError}
        FormHelperTextProps={{
          className: clsx(css.helperText, helperTextClassName)
        }}
        InputProps={{
          classes: {
            root: !fullWidth ? css.field : `${css.field} ${css.fullWidth}`,
            input: startAdornment ? css.withStartAdornment : css.input,
            multiline: css.multiline,
            error: css.error
          },
          startAdornment: startAdornment || null,
          type: rest?.type || 'text',
          ...icon
        }}
        inputProps={{
          'aria-label': ariaLabel,
          'aria-required': required,
          'data-testid': testId || `testid-input${name ? `-${name}` : ''}`,
          readOnly,
          pattern: pattern || undefined,
          ...maxLengthProp,
          ...minLengthProp,
          onChange: (e: any) => {
            if (maxLength) {
              if (e?.target?.value?.length <= maxLength) {
                return;
              }
            }

            field?.onChange?.(e);
          }
        }}
        variant={variant}
        inputRef={inputRef || null}
        onFocus={onFocus}
        onBlur={onBlur}
        fullWidth={fullWidth}
        autoComplete={autoComplete || 'off'}
        autoFocus={autoFocus || false}
        disabled={disabled}
        multiline={multiline}
        minRows={minRows}
        onKeyDown={onKeyDownCallback}
        helperText={computedHelperText}
      />
    );

  return (
    <FormGroup style={{
      width: containerWidth || ''
    }}
    >
      <Label htmlFor={rest?.id} className={css.fieldLabel}>
        {label || ariaLabel || ''}
        {extraInformation?.text ? (
          <IconButton
            size="small"
            aria-label="Extra information"
            className={css.informationIcon}
            onClick={onExtraInformationIconClicked}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        ) : <div className={css.iconPlaceholder} />}
        <Typography component="i" variant="caption" color="textSecondary">
          {showOptionalLabel ? ' (optional)' : ''}
        </Typography>
      </Label>
      <FormControl fullWidth error={hasError}>
        <Controller
          name={fieldName}
          render={({field}) => renderInput(field)}
          rules={{
            required: {
              value: required,
              message: 'This field is required'
            },
            pattern: pattern ? {
              value: new RegExp(pattern),
              message: invalidMessage || 'Please enter a valid value'
            } : undefined
          }}
          control={baseControl}
          defaultValue={defaultValue || ''}
        />
      </FormControl>
      <InfoRail
        visible={showingExtraInformation}
        onClose={onExtraInformationIconClicked}
        title={extraInformation?.title || ''}
      >
        <p
          className={css.extraInformationContent}
          dangerouslySetInnerHTML={{
            __html: extraInformationWithLinks || ''
          }}
        />
      </InfoRail>
    </FormGroup>
  );
};

export default Input;
